export const GUIDES = [
  {
    slug: "how-do-i-know-when-my-parent-needs-more-help",
    title: "How Do I Know When My Parent Needs More Help?",
    excerpt:
      "There is rarely one clear moment that tells you it's time. These quieter signals, in daily routines, the home itself, and your own worry, are often what families notice first.",
    minutes: 5,
    sections: [
      {
        h: "It usually starts quietly",
        p: [
          "Most families don't get a clear signal that a parent needs more support. Instead, there's a slow accumulation of small things: a forgotten bill, a thinner refrigerator, a story that doesn't quite add up, a garden that isn't being tended the way it always was.",
          "If you're asking the question at all, something has probably already caught your attention. That instinct is worth listening to.",
        ],
      },
      {
        h: "Signals families often notice first",
        list: [
          "Everyday tasks, like cooking, laundry, medications, and appointments, are being missed or put off.",
          "The home feels different: unopened mail, undone repairs, less tidiness than usual.",
          "Memory changes show up in small ways: repeated questions, missed engagements, confusion about familiar things.",
          "There's been a fall, a near-miss, or a growing fear of falling.",
          "Your parent is more isolated than they used to be, and activities they enjoyed have quietly stopped.",
          "You find yourself checking in more often, worrying between calls, or carrying more and more of the coordination yourself.",
        ],
        p: [
          "None of these signs on their own means a move is needed, and that's not the only possible answer. Sometimes the right response is more help at home. Sometimes it's a different living arrangement. The point of noticing is simply this: it may be time to understand the options.",
        ],
      },
      {
        h: "Understanding the level of care, together",
        p: [
          "Many families stall here, because they think they must first diagnose the correct category, whether that's Board & Care, Independent Living, Assisted Living, or caregiver support, before anyone can help them.",
          "You don't. A good first conversation is about the person: what their days look like, what has changed, what matters to them, and what your family is worried about. From there, the appropriate options become much clearer.",
        ],
      },
      {
        h: "A gentle way to start the conversation",
        list: [
          "Begin with something specific rather than general: \"I noticed the mail piling up. How are things feeling day to day?\"",
          "Ask about their worries, not just their needs: what would make life feel easier, safer, less lonely?",
          "Include them in every step. The goal is more support and more say for them, not less.",
        ],
      },
      {
        h: "How CareCraft can help",
        p: [
          "CareCraft helps families in Los Angeles understand and explore senior care options, and narrow the possibilities to trusted options worth considering, based on the person's needs, preferences, location, and circumstances.",
          "Our guidance is complimentary to families. Share what's happening, and we'll take it from there.",
        ],
      },
    ],
  },
  {
    slug: "what-happens-when-a-parent-can-no-longer-live-alone",
    title: "What Happens When a Parent Can No Longer Live Alone?",
    excerpt:
      "When living alone stops working, the path forward is rarely a single option. Here is an honest look at what families can consider, and how to think about each one.",
    minutes: 6,
    sections: [
      {
        h: "First, take a breath",
        p: [
          "Realizing that a parent can no longer safely live alone is one of the heavier moments a family can face. It often arrives alongside a hospital discharge, a fall, or a slow slide of small warnings, and it tends to come with time pressure and guilt in equal measure.",
          "Here is what experienced guides will tell you: there are almost always more options than the family first imagines, and almost none of the decisions has to be made in a single day.",
        ],
      },
      {
        h: "The options families usually weigh",
        p: [
          "The right care looks different for every person. These are the possibilities that usually come up in a first conversation:",
        ],
        list: [
          "More support at home (caregiver support): when home is still the right place and everyday assistance could make it workable again.",
          "Board & Care: a smaller residential setting, often in a home-like environment, with day-to-day assistance and closer familiarity with staff.",
          "Independent Living: for parents who remain largely independent but want the convenience, community, and social opportunities of senior living.",
          "Assisted Living: a residential community for older adults who value independence but need regular help with some everyday activities.",
        ],
      },
      {
        h: "Questions that help a family think clearly",
        list: [
          "What does an ordinary day look like now, and what does it need to look like to be safe and good?",
          "Which changes are temporary (a recovery, an adjustment) and which look lasting?",
          "What matters most to your parent: familiar surroundings, community, proximity to family, a particular budget range?",
          "What would \"supported\" look like, rather than \"moved\"?",
          "What would make this decision feel considered rather than rushed?",
        ],
      },
      {
        h: "You can explore without committing",
        p: [
          "Understanding your options is not the same as choosing one. Many families talk through their situation simply to understand what exists, and decide later, or decide that more support at home is the right answer for now.",
          "A good guide will narrow the possibilities to a short, personally vetted list worth considering, while keeping the family's needs, preferences, and circumstances at the center of the process.",
        ],
      },
      {
        h: "How CareCraft can help",
        p: [
          "CareCraft helps families in Los Angeles understand what their options may be when living alone is no longer working, coordinate tours, and think through the decision, with the final choice always remaining with the family.",
          "Our guidance is complimentary to families. Tell us what's happening, and we'll take it from there.",
        ],
      },
    ],
  },
];

export const UPCOMING_GUIDE_TOPICS = [
  "Board & Care vs. Assisted Living: What's the Difference?",
  "What Should I Look for When Touring a Senior Care Home?",
  "Preparing for Senior Care After a Hospital Discharge",
];

export function getGuide(slug) {
  return GUIDES.find((g) => g.slug === slug);
}
